import { useState } from 'react'
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table'
import { format, parseISO, isValid } from 'date-fns'
import { es } from 'date-fns/locale'
import { DataTable } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Eye, Paperclip } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EstadoBadge } from './EstadoBadge'
import type { ComunicacionResumen } from '@/types/afip'

const columnHelper = createColumnHelper<ComunicacionResumen>()

interface ComunicacionesListProps {
  comunicaciones: ComunicacionResumen[]
  isLoading: boolean
  onVerDetalle: (comunicacion: ComunicacionResumen) => void
}

/** Formatea "2025-07-09 21:05:25" a "09/07/2025 21:05". Tolerante a strings inválidos. */
function formatearFecha(value: string): string {
  if (!value) return '—'
  const isoLike = value.includes('T') ? value : value.replace(' ', 'T')
  const d = parseISO(isoLike)
  return isValid(d) ? format(d, "dd/MM/yyyy HH:mm", { locale: es }) : value
}

export function ComunicacionesList({
  comunicaciones,
  isLoading,
  onVerDetalle,
}: ComunicacionesListProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'fechaEmision', desc: true },
  ])

  const columns = [
    columnHelper.accessor('fechaEmision', {
      header: 'Fecha',
      cell: (info) => {
        const noLeida = info.row.original.estado === 'NO_LEIDA'
        return (
          <div className="flex items-center gap-2">
            {noLeida && (
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600"
                aria-hidden="true"
              />
            )}
            <span className={cn('text-sm', noLeida && 'font-medium')}>
              {formatearFecha(info.getValue())}
            </span>
          </div>
        )
      },
      size: 160,
      sortingFn: 'alphanumeric',
    }),
    columnHelper.accessor('asunto', {
      header: 'Asunto',
      cell: (info) => {
        const noLeida = info.row.original.estado === 'NO_LEIDA'
        const valor = info.getValue()
        return (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 max-w-[480px]">
                  <span
                    className={cn(
                      'truncate text-sm',
                      noLeida && 'font-semibold',
                    )}
                  >
                    {valor}
                  </span>
                  {info.row.original.tieneAdjunto && (
                    <Paperclip
                      className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      aria-label="Tiene adjunto"
                    />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="text-xs leading-relaxed">{valor}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      },
    }),
    columnHelper.accessor('sistemaPublicador', {
      header: 'Origen',
      cell: (info) => (
        <span className="text-sm text-muted-foreground">{info.getValue()}</span>
      ),
      size: 200,
    }),
    columnHelper.accessor('estado', {
      header: 'Estado',
      cell: (info) => (
        <EstadoBadge estado={info.getValue()} raw={info.row.original.estadoRaw} />
      ),
      size: 120,
      sortingFn: 'alphanumeric',
    }),
    columnHelper.display({
      id: 'acciones',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onVerDetalle(row.original)}
          aria-label={`Ver detalle de ${row.original.asunto}`}
        >
          <Eye className="h-4 w-4" />
        </Button>
      ),
      size: 60,
      enableSorting: false,
    }),
  ]

  const table = useReactTable({
    data: comunicaciones,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <DataTable
      table={table}
      isLoading={isLoading}
      emptyMessage="No hay comunicaciones de ARCA."
    />
  )
}
